import { Box, Collapse, Indicator } from '@mantine/core';
import { useDisclosure, useHover } from '@mantine/hooks';
import cx from 'clsx';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation, useMatch } from 'react-router-dom';
import { useBot, useMedia } from '@mygpt/app';
import { prefixId } from '@/config/links';
import { useGPTStore } from '@/store';
import { useNavStore } from '@/store/nav';
import { LinkItem } from '@/types/nav-item';
import { BetaBadge } from '@/components/Common/BetaBadge';
import NavGroup from './NavGroup';
import { QuotaInfo } from '@/types/interface';

export default (props: LinkItem & { quotaInfo: QuotaInfo }) => {
  const {
    activeIcon: ActiveIcon,
    icon: Icon,
    label,
    link,
    links,
    target,
    children,
    permissionKey,
    showBeta,
    download,
  } = props;
  const { t } = useTranslation();
  const { botId } = useBot();
  const { setNavbarOpened } = useNavStore();
  const { hovered, ref } = useHover<HTMLAnchorElement>();
  const match = useMatch(link || '');
  const isActive = !!match;
  const location = useLocation();

  const { userInfo, quotaInfo } = useGPTStore((state) => state);
  const { isMobile } = useMedia();

  const checkBotLinkReplaceId = (link: string) => {
    if (!botId) return link;
    return link.replace(prefixId, botId);
  };

  // 检查子菜单是否有激活状态
  const hasActiveChild = useMemo(() => {
    if (!children) return false;
    return children.some((child) => {
      const childLink = checkBotLinkReplaceId(child?.link || '');
      return location.pathname === childLink;
    });
  }, [children, location.pathname, botId]);

  const [childrenOpened, { toggle: toggleChildren }] = useDisclosure(true);

  const hasInvite = useMemo(() => {
    const inviteValue = userInfo?.userconfigs.find((config) => config.key === 'invite_notification');
    return inviteValue && inviteValue.value === 'True';
  }, [userInfo]);

  // 检查权限的函数
  const checkPermission = (permissionKey: string): boolean => {
    if (!quotaInfo?.active_plan_subscription?.plan) return false;
    const plan = quotaInfo.active_plan_subscription.plan;
    return plan[permissionKey as keyof typeof plan] === true;
  };

  // 判断菜单项是否有权限
  const hasPermission = useMemo(() => {
    if (!permissionKey) return true; // 没有权限要求，默认有权限
    return checkPermission(permissionKey);
  }, [permissionKey, quotaInfo]);

  if (links) {
    return <NavGroup {...props} />;
  }

  // 如果有子菜单，渲染带展开功能的菜单项
  if (children && children.length > 0) {
    return (
      <Box>
        <div
          className={cx(
            'flex items-center p-3 rounded-lg transition-all cursor-pointer',
            hasPermission ? 'hover:bg-violet-50 hover:text-violet-500' : 'opacity-50 cursor-not-allowed text-gray-400'
          )}
          onClick={hasPermission ? toggleChildren : undefined}
        >
          {Icon && (
            <Indicator
              color="red.5"
              size={12}
              position="bottom-end"
              withBorder
              disabled={!(hasInvite && link === '/collaborative')}
            >
              {ActiveIcon ? hasActiveChild ? <ActiveIcon /> : <Icon /> : <Icon size="1.1rem" />}
            </Indicator>
          )}
          <span className={cx('text-sm ml-2')}>{t(label)}</span>
        </div>

        <Collapse in={childrenOpened}>
          <Box pl={20} mt={4}>
            {children.map((child, index) => {
              const childHasPermission = child.permissionKey ? checkPermission(child.permissionKey) : true;
              const childIsActive = location.pathname === checkBotLinkReplaceId(child?.link || '');

              return (
                <NavLink
                  key={index}
                  to={childHasPermission ? checkBotLinkReplaceId(child?.link || '') : '#'}
                  target={child.target}
                  className={({ isActive }) => {
                    return cx(
                      'flex items-center p-2 pl-3 rounded-lg transition-all mb-1',
                      !childHasPermission
                        ? 'opacity-50 cursor-not-allowed text-gray-400'
                        : isActive || childIsActive
                        ? 'bg-violet-100 text-violet-500'
                        : 'hover:bg-violet-50 hover:text-violet-500'
                    );
                  }}
                  onClick={(e) => {
                    if (!childHasPermission) {
                      e.preventDefault();
                      return;
                    }
                    isMobile && setNavbarOpened(false);
                  }}
                >
                  {child.icon && (
                    <div className="flex items-center justify-center w-6 h-6">
                      {child.activeIcon ? (
                        childIsActive ? (
                          <child.activeIcon />
                        ) : (
                          <child.icon />
                        )
                      ) : (
                        <child.icon size="1rem" />
                      )}
                    </div>
                  )}
                  <span className={cx('text-sm ml-2')}>{t(child.label)}</span>
                </NavLink>
              );
            })}
          </Box>
        </Collapse>
      </Box>
    );
  }

  // 下载类型的菜单项
  if (download) {
    return (
      <a
        href={checkBotLinkReplaceId(link || '')}
        download
        ref={ref}
        className={cx(
          'flex items-center p-3 rounded-lg transition-all no-underline',
          'hover:bg-violet-50 hover:text-violet-500'
        )}
        onClick={() => {
          isMobile && setNavbarOpened(false);
        }}
      >
        {Icon && (
          <Indicator color="red.5" size={12} position="bottom-end" withBorder disabled>
            {ActiveIcon ? hovered ? <ActiveIcon /> : <Icon /> : <Icon size="1.1rem" />}
          </Indicator>
        )}
        <span className={cx('text-sm ml-2 flex-1')}>{t(label)}</span>
        {showBeta && <BetaBadge />}
      </a>
    );
  }

  // 原有的单级菜单项
  return (
    <NavLink
      to={hasPermission ? checkBotLinkReplaceId(link || '') : '#'}
      target={target}
      ref={ref}
      className={({ isActive, isPending }) => {
        return cx(
          'flex items-center p-3 rounded-lg transition-all',
          !hasPermission
            ? 'opacity-50 cursor-not-allowed text-gray-400'
            : isPending
            ? 'pending'
            : isActive
            ? 'bg-violet-100 text-violet-500'
            : !Icon
            ? 'text-gray-500'
            : 'hover:bg-violet-50 hover:text-violet-500'
        );
      }}
      onClick={(e) => {
        if (!hasPermission) {
          e.preventDefault();
          return;
        }
        isMobile && setNavbarOpened(false);
      }}
    >
      {Icon && (
        <Indicator
          color="red.5"
          size={12}
          position="bottom-end"
          withBorder
          disabled={!(hasInvite && link === '/collaborative')}
        >
          {ActiveIcon ? hovered || isActive ? <ActiveIcon /> : <Icon /> : <Icon size="1.1rem" />}
        </Indicator>
      )}
      <span className={cx('text-sm ml-2 flex-1')}>{t(label)}</span>
      {showBeta && <BetaBadge />}
    </NavLink>
  );
};
